import { useEffect, useState } from 'react';
import { Bell, Send, Search, Users } from 'lucide-react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import AdminLayout from '@/components/AdminLayout';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/hooks/useI18n';
import { formatDate, type Profile, type Notification } from '@/lib/index';

interface NotificationWithRecipient extends Notification {
  recipient_name?: string;
  recipient_email?: string;
}

export default function AdminNotifications() {
  const { t } = useI18n();
  const { toast } = useToast();
  const { profile } = useAuth();
  const [mode, setMode] = useState<'broadcast' | 'targeted'>('broadcast');
  const [clients, setClients] = useState<Profile[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [notifType, setNotifType] = useState<'info' | 'success' | 'warning' | 'alert'>('info');
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [recentNotifications, setRecentNotifications] = useState<NotificationWithRecipient[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    if (!profile?.is_admin) return;
    loadClients();
    loadRecentNotifications();
  }, [profile]);

  const loadClients = async () => {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, email, first_name, last_name, phone, date_of_birth, address, city, country, postcode, kyc_status, plan, is_admin, id_document_type, id_document_front_url, selfie_url, created_at, updated_at')
      .eq('is_admin', false)
      .order('first_name');
    if (!error && data) {
      setClients(data as Profile[]);
    }
  };

  const loadRecentNotifications = async () => {
    setLoadingHistory(true);
    const { data, error } = await supabase
      .from('notifications')
      .select('*, profiles!notifications_user_id_fkey(first_name, last_name, email)')
      .order('created_at', { ascending: false })
      .limit(20);
    
    if (!error && data) {
      const formatted = data.map((n: any) => ({
        id: n.id,
        user_id: n.user_id,
        title: n.title,
        message: n.message,
        type: n.type,
        is_read: n.is_read,
        created_at: n.created_at,
        recipient_name: n.profiles ? `${n.profiles.first_name} ${n.profiles.last_name}` : 'Nieznany',
        recipient_email: n.profiles?.email || '',
      }));
      setRecentNotifications(formatted);
    }
    setLoadingHistory(false);
  };

  const handleSendNotification = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim() || !message.trim()) {
      toast({
        title: 'Błąd',
        description: 'Wypełnij wszystkie pola',
        variant: 'destructive',
      });
      return;
    }

    setSending(true);

    try {
      if (mode === 'broadcast') {
        const notifications = clients.map(client => ({
          user_id: client.id,
          title: title.trim(),
          message: message.trim(),
          type: notifType,
          is_read: false,
        }));

        const { error } = await supabase
          .from('notifications')
          .insert(notifications);

        if (error) throw error;

        toast({
          title: 'Sukces',
          description: `Powiadomienie wysłane do wszystkich klientów (${clients.length})`,
        });
      } else {
        if (!selectedUserId) {
          toast({
            title: 'Błąd',
            description: 'Wybierz klienta',
            variant: 'destructive',
          });
          setSending(false);
          return;
        }

        const { error } = await supabase
          .from('notifications')
          .insert({
            user_id: selectedUserId,
            title: title.trim(),
            message: message.trim(),
            type: notifType,
            is_read: false,
          });

        if (error) throw error;

        const client = clients.find(c => c.id === selectedUserId);
        toast({
          title: 'Sukces',
          description: `Powiadomienie wysłane do ${client?.first_name} ${client?.last_name}`,
        });
      }

      setTitle('');
      setMessage('');
      setSelectedUserId('');
      loadRecentNotifications();
    } catch (error) {
      toast({
        title: 'Błąd',
        description: error instanceof Error ? error.message : 'Wystąpił błąd',
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  const filteredClients = clients.filter(client => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      client.first_name?.toLowerCase().includes(query) ||
      client.last_name?.toLowerCase().includes(query) ||
      client.email?.toLowerCase().includes(query)
    );
  });

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'info': return 'bg-info-light text-info border-info/20';
      case 'success': return 'bg-success-light text-success border-success/20';
      case 'warning': return 'bg-warning-light text-warning border-warning/20';
      case 'alert': return 'bg-danger-light text-danger border-danger/20';
      default: return 'bg-muted/10 text-muted-foreground border-border';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'info': return 'ℹ️';
      case 'success': return '✅';
      case 'warning': return '⚠️';
      case 'alert': return '🚨';
      default: return '📢';
    }
  };

  return (
    <AdminLayout title="Powiadomienia">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">Powiadomienia</h1>
        <p className="text-muted-foreground">Wysyłaj powiadomienia do klientów</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="border-border/50 h-fit">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-gold" />
              Wyślij powiadomienie
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSendNotification} className="space-y-6">
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setMode('broadcast')}
                  className={`flex items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all ${
                    mode === 'broadcast'
                      ? 'bg-gold/10 border-gold text-gold'
                      : 'bg-muted/30 border-border text-muted-foreground hover:border-gold/50'
                  }`}
                >
                  <Users className="w-5 h-5" />
                  <span className="font-semibold">Wszyscy klienci</span>
                </button>
                <button
                  type="button"
                  onClick={() => setMode('targeted')}
                  className={`flex items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all ${
                    mode === 'targeted'
                      ? 'bg-gold/10 border-gold text-gold'
                      : 'bg-muted/30 border-border text-muted-foreground hover:border-gold/50'
                  }`}
                >
                  <Send className="w-5 h-5" />
                  <span className="font-semibold">Pojedynczy klient</span>
                </button>
              </div>

              {mode === 'targeted' && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-3"
                >
                  <Label>Wybierz klienta</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Szukaj klienta..."
                      className="pl-10"
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto border border-border rounded-xl divide-y divide-border">
                    {filteredClients.length === 0 ? (
                      <div className="p-4 text-center text-sm text-muted-foreground">
                        Brak klientów
                      </div>
                    ) : (
                      filteredClients.map((client) => (
                        <button
                          key={client.id}
                          type="button"
                          onClick={() => setSelectedUserId(client.id)}
                          className={`w-full flex items-center gap-3 p-3 text-left transition-colors hover:bg-muted/50 ${
                            selectedUserId === client.id ? 'bg-gold/10' : ''
                          }`}
                        >
                          <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center text-gold font-bold shrink-0">
                            {client.first_name?.[0]?.toUpperCase() || 'U'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-foreground truncate">
                              {client.first_name} {client.last_name}
                            </p>
                            <p className="text-sm text-muted-foreground truncate">{client.email}</p>
                          </div>
                          {selectedUserId === client.id && (
                            <Badge className="bg-gold text-navy">✓</Badge>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                </motion.div>
              )}

              <div className="space-y-2">
                <Label htmlFor="notif-title">Tytuł</Label>
                <Input
                  id="notif-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Tytuł powiadomienia"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="notif-message">Wiadomość</Label>
                <Textarea
                  id="notif-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Treść powiadomienia"
                  rows={4}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Typ powiadomienia</Label>
                <Select value={notifType} onValueChange={(v) => setNotifType(v as any)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="info">
                      <span className="flex items-center gap-2">
                        <span>ℹ️</span>
                        <span>Informacja</span>
                      </span>
                    </SelectItem>
                    <SelectItem value="success">
                      <span className="flex items-center gap-2">
                        <span>✅</span>
                        <span>Sukces</span>
                      </span>
                    </SelectItem>
                    <SelectItem value="warning">
                      <span className="flex items-center gap-2">
                        <span>⚠️</span>
                        <span>Ostrzeżenie</span>
                      </span>
                    </SelectItem>
                    <SelectItem value="alert">
                      <span className="flex items-center gap-2">
                        <span>🚨</span>
                        <span>Alert</span>
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className={`p-4 rounded-xl border ${getTypeColor(notifType)}`}>
                <p className="font-semibold mb-1">
                  {getTypeIcon(notifType)} {title || 'Tytuł powiadomienia'}
                </p>
                <p className="text-sm opacity-90">{message || 'Treść powiadomienia'}</p>
              </div>

              <Button
                type="submit"
                disabled={sending}
                className="w-full bg-gold hover:bg-gold-dim text-navy font-bold"
              >
                <Send className="w-4 h-4 mr-2" />
                {sending
                  ? 'Wysyłanie...'
                  : mode === 'broadcast'
                  ? `Wyślij do wszystkich (${clients.length})`
                  : 'Wyślij powiadomienie'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-muted-foreground" />
              Ostatnie powiadomienia ({recentNotifications.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingHistory ? (
              <div className="text-center py-8 text-muted-foreground">Ładowanie...</div>
            ) : recentNotifications.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">Brak powiadomień</div>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {recentNotifications.map((notif) => (
                  <div
                    key={notif.id}
                    className="p-4 rounded-xl border border-border bg-card hover:bg-muted/30 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2">
                        <Badge className={getTypeColor(notif.type)}>
                          {getTypeIcon(notif.type)} {notif.type}
                        </Badge>
                        <span className="text-xs text-muted-foreground">{formatDate(notif.created_at)}</span>
                      </div>
                    </div>
                    <p className="font-semibold text-foreground mb-1">{notif.title}</p>
                    <p className="text-sm text-muted-foreground mb-2">{notif.message}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="font-medium">Do:</span>
                      <span>{notif.recipient_name}</span>
                      <span className="opacity-60">({notif.recipient_email})</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}